import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Grid,
  Typography,
  Box,
  Divider,
  Paper,
} from '@mui/material';
import ShowMoreText from 'react-show-more-text';
import { useTranslation } from 'next-i18next';

import GetStartedBtn from 'views/RentMyMotorcycleView/components/GetStartedBtn';

const PREFIX = 'WhyEagleShare';

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  title: `${PREFIX}-title`,
  partTitle: `${PREFIX}-partTitle`,
  partDesc: `${PREFIX}-partDesc`,
  partDivider: `${PREFIX}-partDivider`,
  getStartedBox: `${PREFIX}-getStartedBox`,
  detailsText: `${PREFIX}-detailsText`,
  readMore: `${PREFIX}-readMore`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    marginTop: theme.spacing(13),
    paddingBottom: theme.spacing(13),
    maxWidth: theme.spacing(166.25),
    [theme.breakpoints.up('lg')]: {
      paddingBottom: theme.spacing(16.75),
      marginTop: theme.spacing(18),
    },
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(5.5),
      paddingBottom: theme.spacing(10),
    },
  },

  [`& .${classes.title}`]: {
    fontSize: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(4.5),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(36),
      marginBottom: theme.spacing(7.5),
    },
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(10.25),
    },
  },

  [`& .${classes.partTitle}`]: {
    fontSize: theme.typography.pxToRem(24),
    textAlign: 'center',
    [theme.breakpoints.only('sm')]: {
      fontSize: theme.typography.pxToRem(20),
    },
  },

  [`& .${classes.partDesc}`]: {
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.grey[300],
    marginTop: theme.spacing(1.5),
    textAlign: 'center',
    [theme.breakpoints.only('sm')]: {
      fontSize: theme.typography.pxToRem(16),
    },
  },

  [`& .${classes.partDivider}`]: {
    marginTop: theme.spacing(6.25),
    marginBottom: theme.spacing(1.25),
    height: theme.spacing(0.625),
    backgroundColor: theme.palette.grey[300],
    width: '20%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  [`& .${classes.getStartedBox}`]: {
    marginTop: theme.spacing(9),
    textAlign: 'center',
    letterSpacing: theme.typography.pxToRem(0.5),
    lineHeight: theme.typography.pxToRem(24),
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(4.5),
    },
  },
  [`& .${classes.detailsText}`]: {
    marginTop: theme.spacing(4.25),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(8.75),
    },
    [theme.breakpoints.up('lg')]: {
      textAlign: 'left',
      marginTop: theme.spacing(6.25),
    },
  },

  [`& .${classes.readMore}`]: {
    textDecoration: 'underline',
    color: theme.palette.secondary.main,
  },
}));

const WhyEagleShare = () => {
  const { t } = useTranslation('fe_er_rent_my_motorcycle');

  return (
    <Root>
      <Container maxWidth="xl" classes={{ root: classes.mainContainer }}>
        <Typography variant="h2" classes={{ root: classes.title }}>
          {t('fe_er_rent_my_motorcycle:why_eagleShare_title')}
        </Typography>

        <Grid container alignItems="stretch" spacing={8}>
          <Grid item xs={12} lg={4}>
            <Typography variant="h3" classes={{ root: classes.partTitle }}>
              {t('fe_er_rent_my_motorcycle:why_eagleShare_parts.title1')}
            </Typography>
            <Typography classes={{ root: classes.partDesc }}>
              {t('fe_er_rent_my_motorcycle:why_eagleShare_parts.desc1')}
            </Typography>
            <Paper sx={{ display: { md: 'block', lg: 'none' } }} elevation={0}>
              <Divider classes={{ root: classes.partDivider }} />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Typography variant="h3" classes={{ root: classes.partTitle }}>
              {t('fe_er_rent_my_motorcycle:why_eagleShare_parts.title2')}
            </Typography>
            <Typography classes={{ root: classes.partDesc }}>
              {t('fe_er_rent_my_motorcycle:why_eagleShare_parts.desc2')}
            </Typography>
            <Paper sx={{ display: { md: 'block', lg: 'none' } }} elevation={0}>
              <Divider classes={{ root: classes.partDivider }} />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Typography variant="h3" classes={{ root: classes.partTitle }}>
              {t('fe_er_rent_my_motorcycle:why_eagleShare_parts.title3')}
            </Typography>
            <Typography classes={{ root: classes.partDesc }}>
              {t('fe_er_rent_my_motorcycle:why_eagleShare_parts.desc3')}
            </Typography>
          </Grid>
        </Grid>
        <Paper sx={{ display: { sm: 'none', md: 'block' } }} elevation={0}>
          <Typography classes={{ root: classes.detailsText }}>
            {t('fe_er_rent_my_motorcycle:passionate_about_bikes')}
          </Typography>
        </Paper>
        <Paper sx={{ display: { sm: 'block', md: 'none' } }} elevation={0}>
          <ShowMoreText
            lines={6}
            more={t('fe_er_rent_my_motorcycle:read_more')}
            less={t('fe_er_rent_my_motorcycle:read_less')}
            className={classes.detailsText}
            anchorClass={classes.readMore}
            expanded={false}>
            {t('fe_er_rent_my_motorcycle:passionate_about_bikes')}
          </ShowMoreText>
        </Paper>

        <Box className={classes.getStartedBox}>
          <GetStartedBtn t={t} />
        </Box>
      </Container>
    </Root>
  );
};

export default WhyEagleShare;
